import * as React from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/util/cn';
import { isNewsHref } from '@/util/isNewsHref';
import { Heading } from '@/components/ui/heading';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

// Container for N items. One-row scroll-snap rail on mobile,
// configurable layout at desktop:
//
//   - "carousel"   chevron-nav scroll-snap rail (default)
//   - "grid"       CSS grid, all items visible, no pagination
//   - "grid" + desktopPageSize:
//                  CSS grid, only `desktopPageSize` items visible at
//                  a time, prev/next pagination control below
//
// Mobile is always the horizontal swipe (peek of the next card
// visible). "Carousel" is one of three desktop UIs this component
// supports — hence the broader `ItemsContainer` name. Each item is
// wrapped by the caller in `<ItemsContainer.Item>`.
//
// Source pattern: extended from the redesign home Carousel prototype.

const COLUMNS_TO_CLASS: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
};

// Per-column basis classes for the lg+ carousel rail. Picked from a
// fixed table so Tailwind can statically extract the arbitrary calc()
// values — building them with template literals would not survive the
// JIT pass.
const CAROUSEL_LG_BASIS: Record<number, string> = {
  2: 'lg:basis-[calc((100%-1.25rem)/2)]',
  3: 'lg:basis-[calc((100%-2.5rem)/3)]',
  4: 'lg:basis-[calc((100%-3.75rem)/4)]',
  5: 'lg:basis-[calc((100%-5rem)/5)]',
};

// Per-column basis context: Root publishes the chosen desktopColumns
// so Item can render the matching rail width without a prop drill.
const ItemsContainerContext = React.createContext<number>(3);

export type DesktopLayout = 'carousel' | 'grid';

export type ItemsContainerProps = {
  title?: string;
  /** Replaces the title slot in the header row with arbitrary content
   *  (e.g. a CategoryChips rail). Renders inside a flex-1 wrapper so
   *  the consumer can take the full row width on the left while the
   *  built-in chevron-nav / viewAll Link stay right-aligned. */
  headerLeft?: React.ReactNode;
  viewAllHref?: string;
  viewAllLabel?: string;
  /** "carousel" (default) keeps the scroll-snap rail at desktop.
   *  "grid" switches to a CSS grid at md+ with `desktopColumns` per row. */
  desktopLayout?: DesktopLayout;
  /** Columns to use at desktop. In `grid` mode this drives the
   *  md:grid-cols-N class. In `carousel` mode it drives the lg-rail
   *  basis (how many cards are visible per row at lg+). Default 3.
   *  Capped at 5. */
  desktopColumns?: 1 | 2 | 3 | 4 | 5;
  /** When set together with `desktopLayout="grid"`, only this many
   *  items render at a time on desktop. A prev/next pager appears
   *  below the grid. Mobile is unaffected — always scrolls all items. */
  desktopPageSize?: number;
  /** Where the prev/next chevrons render on desktop carousel mode.
   *  - "header" (default): top-right of the header row, with the title.
   *  - "overlay": absolutely positioned over the rail, vertically
   *    centered on left + right edges (classic photo-carousel style).
   *  Ignored in grid mode. */
  navStyle?: 'header' | 'overlay';
  ariaLabel?: string;
  children: React.ReactNode;
  /** Optional className applied to the outer <section>. */
  className?: string;
};

const ChipButton = ({
  onClick, label, disabled, children,
}: {
  onClick: () => void;
  label: string;
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background',
      'text-foreground transition hover:bg-muted',
      'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-background',
    )}
  >
    {children}
  </button>
);

const Root = ({
  title,
  headerLeft,
  viewAllHref,
  viewAllLabel,
  desktopLayout = 'carousel',
  desktopColumns = 3,
  desktopPageSize,
  navStyle = 'header',
  ariaLabel,
  children,
  className,
}: ItemsContainerProps) => {
  const { t } = useOTSTranslation('common');
  const resolvedViewAllLabel = viewAllLabel ?? t('common.viewAll');
  const trackRef = React.useRef<HTMLDivElement>(null);

  // Pagination state — only relevant at desktop in grid+pageSize mode.
  // Mobile ignores it entirely (renders all children in the scroll
  // rail), so the state is allowed to drift across breakpoint changes
  // without breaking anything.
  const items = React.Children.toArray(children);
  const isPaginated = desktopLayout === 'grid' && typeof desktopPageSize === 'number' && desktopPageSize > 0;
  const pageSize = isPaginated ? desktopPageSize : items.length;
  const pageCount = isPaginated ? Math.max(1, Math.ceil(items.length / pageSize)) : 1;
  const [page, setPage] = React.useState(0);

  // Tracks whether the rail is parked at the start. Powers the
  // mobile-only "swipe me" chevron hint that fades out the moment the
  // user has scrolled. SSR-safe default of true (initial render is
  // always at position 0).
  const [atStart, setAtStart] = React.useState(true);

  React.useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const onScroll = () => setAtStart(el.scrollLeft < 8);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollBy = React.useCallback((dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.4, 280);
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  }, []);

  const isCarousel = desktopLayout === 'carousel';
  const showHeaderNav = isCarousel && navStyle === 'header';
  const showOverlayNav = isCarousel && navStyle === 'overlay';
  const hasHeader = Boolean(title || headerLeft || viewAllHref || showHeaderNav);

  // The -mx-4 lets the rail bleed past the parent gutter on mobile so
  // the right-side card peek lands at the viewport edge. pl-4 cancels
  // out -mx-4 on the left, so the first item lines up flush with the
  // surrounding content (heading, body text). pr-4 keeps the right
  // side tight so the next-card peek (controlled by Item basis) lines
  // up. scroll-padding-left matches pl-4 so snap points align with
  // the visible left edge.
  const trackBase = cn(
    '-mx-4 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth',
    'pl-4 pr-4 pb-2 [scroll-padding-left:1rem]',
    '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
  );
  const trackGridAtMd = `md:mx-0 md:grid ${COLUMNS_TO_CLASS[desktopColumns]} md:gap-5 md:overflow-visible md:px-0 md:pb-0`;

  return (
    <ItemsContainerContext.Provider value={desktopColumns}>
      <section className={className} aria-label={ariaLabel}>
        {hasHeader && (
        <div className="mb-4 flex items-center justify-between gap-4">
          {(() => {
            if (headerLeft) {
              return <div className="min-w-0 flex-1">{headerLeft}</div>;
            }
            if (title) {
              return <Heading level={2}>{title}</Heading>;
            }
            return <span />;
          })()}
          <div className="flex shrink-0 items-center gap-3">
            {viewAllHref && (
              // News CMS links leave this app — render as a plain <a> so
              // next/link doesn't RSC-prefetch a 404 (see isNewsHref).
              isNewsHref(viewAllHref) ? (
                <a
                  href={viewAllHref}
                  className="hidden text-sm font-semibold text-primary hover:underline sm:inline"
                >
                  {resolvedViewAllLabel}
                </a>
              ) : (
                <Link
                  href={viewAllHref}
                  className="hidden text-sm font-semibold text-primary hover:underline sm:inline"
                >
                  {resolvedViewAllLabel}
                </Link>
              )
            )}
            {showHeaderNav && (
              // Hidden on mobile — the peeking next card makes it
              // visually obvious the row is swipeable, so explicit
              // chevrons would be redundant chrome.
              <div className="hidden items-center gap-1.5 md:flex">
                <ChipButton label={t('common.previous')} onClick={() => scrollBy(-1)}>
                  <ChevronLeft size={18} className="fill-none" />
                </ChipButton>
                <ChipButton label={t('resort.reviews.next')} onClick={() => scrollBy(1)}>
                  <ChevronRight size={18} className="fill-none" />
                </ChipButton>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Mobile always shows ALL items in the scroll rail, regardless
          of pagination. At desktop, the grid CSS in `trackGridAtMd`
          takes over and the rail behavior disappears. The relative
          wrapper anchors the mobile "swipe me" chevron overlay below. */}
        <div className="relative">
          <button
            type="button"
            aria-label={t('resort.reviews.next')}
            tabIndex={-1}
            onClick={() => scrollBy(1)}
            className={cn(
              'absolute right-2 top-1/2 z-10 -translate-y-1/2',
              'inline-flex h-7 w-7 items-center justify-center rounded-full',
              'bg-black/45 text-white shadow-sm backdrop-blur-sm',
              'transition-opacity duration-300 md:hidden',
              atStart ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <ChevronRight size={14} className="fill-none" />
          </button>
          {showOverlayNav && (
            <>
              <button
                type="button"
                aria-label={t('common.previous')}
                onClick={() => scrollBy(-1)}
                className={cn(
                  'absolute left-3 top-1/2 z-10 hidden -translate-y-1/2 md:inline-flex',
                  'h-10 w-10 items-center justify-center rounded-full',
                  'bg-black/45 text-white shadow-md backdrop-blur-sm transition',
                  'hover:bg-black/60',
                )}
              >
                <ChevronLeft size={20} className="fill-none" />
              </button>
              <button
                type="button"
                aria-label={t('resort.reviews.next')}
                onClick={() => scrollBy(1)}
                className={cn(
                  'absolute right-3 top-1/2 z-10 hidden -translate-y-1/2 md:inline-flex',
                  'h-10 w-10 items-center justify-center rounded-full',
                  'bg-black/45 text-white shadow-md backdrop-blur-sm transition',
                  'hover:bg-black/60',
                )}
              >
                <ChevronRight size={20} className="fill-none" />
              </button>
            </>
          )}
          <div
            ref={trackRef}
            data-layout={desktopLayout}
            className={cn(trackBase, desktopLayout === 'grid' && trackGridAtMd)}
          >
            {/* On mobile we render every child in the rail; on desktop
              paginated mode we only show the current page's slice. We
              achieve this by rendering all children but hiding the
              out-of-page ones at desktop only. Keeps DOM stable so
              existing refs / focus aren't blown away on page change. */}
            {isPaginated
              ? items.map((child, i) => {
                const onCurrentPage = i >= page * pageSize && i < (page + 1) * pageSize;
                // `contents` means the wrapper has no box of its own —
                // the child participates in the parent flex/grid layout
                // directly. At md+ in paginated mode, off-page items get
                // `display: none` (which overrides `contents`) so the
                // grid only lays out the current page's slice.
                return (
                  <div
                    key={(child as React.ReactElement).key ?? i}
                    className={cn('contents', !onCurrentPage && 'md:hidden')}
                  >
                    {child}
                  </div>
                );
              })
              : children}
          </div>
        </div>

        {isPaginated && pageCount > 1 && (
        <div className="mt-6 hidden items-center justify-center gap-3 md:flex">
          <ChipButton
            label={t('common.previousPage')}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            <ChevronLeft size={18} className="fill-none" />
          </ChipButton>
          <span className="text-sm font-medium tabular-nums text-muted-foreground">
            {t('common.pageOfPages', { page: page + 1, total: pageCount })}
          </span>
          <ChipButton
            label={t('common.nextPage')}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={page >= pageCount - 1}
          >
            <ChevronRight size={18} className="fill-none" />
          </ChipButton>
        </div>
        )}
      </section>
    </ItemsContainerContext.Provider>
  );
};

export type ItemsContainerItemProps = {
  children: React.ReactNode;
  className?: string;
};

// Mobile basis is the peek-the-next-card width — 90% keeps the peek
// subtle (a sliver of the next card on the right) and lets the
// parent's pl-6 do the heavy lifting for left-edge breathing room.
// In grid mode at desktop, flex-basis / flex-shrink are inert (parent
// is CSS grid), so the same item classes work for both layouts
// without overrides.

const Item = ({ children, className }: ItemsContainerItemProps) => {
  const cols = React.useContext(ItemsContainerContext);
  const lgBasis = CAROUSEL_LG_BASIS[cols] ?? CAROUSEL_LG_BASIS[3];
  return (
    <div
      className={cn(
        'shrink-0 snap-start basis-[90%] sm:basis-[48%]',
        lgBasis,
        className,
      )}
    >
      {children}
    </div>
  );
};

export const ItemsContainer = Object.assign(Root, { Item });
